// MetricTile — neomorphic quick-stat tile with an animated counter. The whole
// tile is a button that navigates to the vertical's page. `definition` ships
// the exact query semantics as a native tooltip so every number on the
// dashboard is self-documenting (screen-field-audit discipline).

import { motion } from 'framer-motion';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import AnimatedCounter from '@/components/shared/AnimatedCounter';

type MetricTileProps = {
  icon: LucideIcon;
  label: string;
  /** undefined = still loading (renders an em dash, no fake zero). */
  value: number | undefined;
  definition: string;
  onClick: () => void;
  /** Copper-highlight the number when > 0 (actionable queue). */
  attention?: boolean;
  /** Stagger index for the entrance animation. */
  index?: number;
};

export default function MetricTile({
  icon: Icon,
  label,
  value,
  definition,
  onClick,
  attention = false,
  index = 0,
}: MetricTileProps): JSX.Element {
  const loaded = value !== undefined;
  const hot = attention && (value ?? 0) > 0;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      title={definition}
      className="neo-raised-hover text-left p-5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className={`neo-chip w-9 h-9 flex items-center justify-center ${hot ? 'neo-pulse-glow' : ''}`}
        >
          <Icon
            className={`w-4 h-4 transition-colors ${hot ? 'text-attention' : 'text-forest/70 group-hover:text-forest'}`}
            aria-hidden
          />
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/0 group-hover:text-forest transition-all group-hover:translate-x-0.5" />
      </div>
      <p
        className={`text-3xl font-bold tracking-tight tabular-nums ${hot ? 'text-attention' : 'text-foreground'}`}
        aria-label={`${label}: ${loaded ? value : 'loading'}`}
      >
        {loaded ? <AnimatedCounter value={value} decimals={0} duration={1} /> : '—'}
      </p>
      <p className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </p>
    </motion.button>
  );
}
